import { BookOpenText, Loader2, Network, Sparkles, Star, StickyNote, Trash2 } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { KpiStatTile } from '@/components/insights/kpi/KpiStatTile'
import { InvestigationCard } from '@/components/investigations/InvestigationDossier'
import { PageHeading } from '@/components/layout/PageShell'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { HypothesesSection } from '@/features/analytic-hypotheses/HypothesesSection'
import { useInvestigationDelete } from '@/features/cases/hooks/useInvestigationDelete'
import {
  useInvestigationsStore,
  useInvestigationsStoreApi,
} from '@/stores/investigations-store-provider'
import { useCaseContext } from '../CaseContext'

interface InvestigationStats {
  total: number
  withNotes: number
  inReport: number
  byAi: number
}

// ─── Page header / states ────────────────────────────────────────────────────

function InvestigationsHeader() {
  const { t } = useTranslation()
  return (
    <PageHeading
      icon={BookOpenText}
      title={t('investigations.page.title')}
      summary={t('investigations.page.summary')}
    />
  )
}

function InvestigationsLoadingState() {
  const { t } = useTranslation()
  return (
    <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
      <Loader2 className="mr-2 size-4 animate-spin" />
      {t('investigations.page.loading')}
    </div>
  )
}

function InvestigationsErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 text-sm">
      <p className="text-destructive">{t('investigations.page.loadFailed')}</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        {t('investigations.page.loadFailedRetry')}
      </Button>
    </div>
  )
}

function InvestigationsEmptyState({ onOpenGraph }: { onOpenGraph: () => void }) {
  const { t } = useTranslation()
  return (
    <Card variant="dashed" className="px-5 py-10 text-center md:px-10 md:py-14">
      <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-2xl border border-primary/40 bg-primary/10 text-primary">
        <BookOpenText className="size-6" />
      </div>
      <h2 className="text-xl font-medium tracking-tight text-foreground">
        {t('investigations.page.empty.title')}
      </h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
        {t('investigations.page.empty.body')}
      </p>
      <div className="mt-5 inline-flex gap-2">
        <Button onClick={onOpenGraph}>
          <Network className="size-3.5" />
          {t('investigations.page.empty.openGraph')}
        </Button>
      </div>
    </Card>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function CaseInvestigationsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { caseId } = useCaseContext()
  const saved = useInvestigationsStore(s => s.saved)
  const isLoading = useInvestigationsStore(s => s.isLoading)
  const error = useInvestigationsStore(s => s.error)
  const storeApi = useInvestigationsStoreApi()
  const { deletingInv, deleting, requestDelete, cancelDelete, confirmDelete } =
    useInvestigationDelete()

  const stats: InvestigationStats = useMemo(() => {
    const initial: InvestigationStats = { total: 0, withNotes: 0, inReport: 0, byAi: 0 }
    return saved.reduce<InvestigationStats>((acc, inv) => {
      acc.total += 1
      if (inv.notes.length > 0) acc.withNotes += 1
      if (inv.includedInReport) acc.inReport += 1
      if (inv.source === 'ai') acc.byAi += 1
      return acc
    }, initial)
  }, [saved])

  const showLoading = isLoading && saved.length === 0
  const showError = !!error && saved.length === 0
  const showEmpty = !showLoading && !showError && saved.length === 0

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <div className="flex flex-1 min-h-0 flex-col gap-6 overflow-y-auto px-4 py-6 md:px-6">
        <InvestigationsHeader />

        <HypothesesSection caseId={caseId} />

        {showLoading ? (
          <InvestigationsLoadingState />
        ) : showError ? (
          <InvestigationsErrorState onRetry={() => storeApi.getState().fetchAll()} />
        ) : showEmpty ? (
          <InvestigationsEmptyState onOpenGraph={() => navigate(`/cases/${caseId}/graph`)} />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <KpiStatTile
                icon={BookOpenText}
                label={t('investigations.page.stats.saved')}
                value={stats.total}
                highlight
              />
              <KpiStatTile
                icon={StickyNote}
                label={t('investigations.page.stats.withNotes')}
                value={stats.withNotes}
              />
              <KpiStatTile
                icon={Star}
                label={t('investigations.page.stats.inReport')}
                value={stats.inReport}
              />
              <KpiStatTile
                icon={Sparkles}
                label={t('investigations.page.stats.byAi')}
                value={stats.byAi}
              />
            </div>

            <div>
              <div className="mb-3 flex items-baseline justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    {t('investigations.page.list.title')}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {t('investigations.page.list.hint')}
                  </div>
                </div>
                <span className="shrink-0 whitespace-nowrap font-mono text-xs text-muted-foreground">
                  {t('investigations.page.list.entries', { count: saved.length })}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {saved.map((inv, i) => (
                  <InvestigationCard
                    key={inv.id}
                    inv={inv}
                    index={i}
                    to={`/cases/${caseId}/investigations/${inv.id}`}
                    onDelete={() => requestDelete(inv)}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <AlertDialog
        open={!!deletingInv}
        onOpenChange={open => {
          if (!open) cancelDelete()
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="size-4 text-destructive" />
              {t('investigations.page.delete.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('investigations.page.delete.description', { name: deletingInv?.title ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting
                ? t('investigations.page.delete.deleting')
                : t('investigations.page.delete.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
